import type { Rect } from './climb-thumbnail';

/**
 * **The DivePeaks mark** on everything the share dialog and editor export: a
 * white disc with the logo and the word "divepeaks" on it. Not removable — the
 * one thing every shared picture and video says about where it was made. In
 * the editor it is on the file made only, never on the preview, in a corner
 * the card leaves free (see `markPositionFor` in `thumbnail-placement.ts`).
 *
 * Sized against the short edge of the frame, so it is the same size on a
 * portrait video and a landscape one.
 */

/** The disc's diameter, as a share of the frame's short edge. */
const DIAMETER = 0.13;
/** Its distance from the frame's edges, as a share of the short edge. */
const MARGIN = 0.035;

const LOGO = 0.5;
const LOGO_OFFSET = -0.1;
const WORD_FONT = 0.135;
const WORD_OFFSET = 0.3;
const WORD_COLOR = '#1f2937';
const FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export const WATERMARK_LOGO_URL = '/icons/favicon.svg';
export const WATERMARK_WORD = 'divepeaks';

/** Where the mark goes: its top-left corner, as fractions of the frame's width and height. */
export interface MarkPosition {
  x: number;
  y: number;
}

/** The mark's diameter on a frame of this size. */
export function watermarkSize(frameWidth: number, frameHeight: number): number {
  return DIAMETER * Math.min(frameWidth, frameHeight);
}

/**
 * Where the mark sits on a frame of this size: where it was put, or the
 * bottom-right corner.
 */
export function watermarkRect(frameWidth: number, frameHeight: number, position?: MarkPosition | null): Rect {
  const size = watermarkSize(frameWidth, frameHeight);
  if (position) return { x: position.x * frameWidth, y: position.y * frameHeight, width: size, height: size };
  const margin = MARGIN * Math.min(frameWidth, frameHeight);
  return { x: frameWidth - margin - size, y: frameHeight - margin - size, width: size, height: size };
}

/** Draw the mark on a frame of this size. Without the logo (not loaded yet) the disc and the word still are. */
export function drawWatermark(
  ctx: CanvasRenderingContext2D,
  frameWidth: number,
  frameHeight: number,
  logo: CanvasImageSource | null,
  position?: MarkPosition | null,
) {
  const { x, y, width: size } = watermarkRect(frameWidth, frameHeight, position);
  const r = size / 2;
  const cx = x + r;
  const cy = y + r;

  ctx.save();
  // A soft shadow, so the disc still has an edge over a white wall.
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = size * 0.08;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.restore();

  ctx.save();
  if (logo) {
    const logoSize = size * LOGO;
    ctx.drawImage(logo, cx - logoSize / 2, cy + size * LOGO_OFFSET - logoSize / 2, logoSize, logoSize);
  }
  ctx.font = `700 ${WORD_FONT * size}px ${FONT}`;
  ctx.fillStyle = WORD_COLOR;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(WATERMARK_WORD, cx, cy + size * WORD_OFFSET);
  ctx.restore();
}
